import { ReactNode } from "react";
import { Control, Controller, FieldValues, Path, useWatch } from "react-hook-form";
import type { RecurrenceInput } from "@myapp/contract";
import { FieldWrapper } from "../../controls/FieldWrapper/FieldWrapper";
import { FormInput } from "../../controls/FormInput";
import { SegmentedControl } from "../../controls/SegmentedControl";
import { ActualTimesSummary } from "../../controls/ActualTimesSummary/ActualTimesSummary";
import { FieldGroup } from "../../controls/FieldGroup/FieldGroup";
import { RecurrenceField } from "../RecurrenceField/RecurrenceField";

/**
 * The „Zeit" section shared by the Termin and the Aufgabe form: when the work starts, the three
 * planned durations, the Ist-Zeitpunkte once the item is completed, and the series rule or scope.
 *
 * One component rather than two copies, because the two forms describe the same reservation —
 * a `ScheduleEntry` — and used to drift: different labels for the same minutes, Serie as its own
 * section in one form and inline in the other. Whatever differs between the two is a prop, and
 * there is deliberately little of it.
 *
 * It lives in `shared/` because features cannot import each other. It knows nothing about statuses
 * (`AppointmentStatus` and `TaskStatus` are different types, and which of them means "completed" is
 * the owning schema's rule), so `showActuals` arrives decided. It is also deliberately free of
 * queries and mutations — `RecurrenceField` sets the precedent.
 *
 * The ten fields it binds exist with identical types on both form schemas; `ScheduleFieldValues`
 * is that contract, and the generic keeps `control` typed to the owning form.
 */
export interface ScheduleFieldValues {
  plannedStart: string;
  plannedTravelDuration: number;
  plannedWorkDuration: number;
  plannedDepartureDuration: number;
  travelStartedAt: string;
  actualStart: string;
  actualEnd: string;
  departureEndedAt: string;
  recurrence: RecurrenceInput | null;
  forFullSeries: boolean;
}

export interface ScheduleFieldsProps<T extends FieldValues & ScheduleFieldValues> {
  control: Control<T>;
  mode: "create" | "edit";
  /**
   * The item is in a completed state, so the four Ist-Zeitpunkte are shown and sent. Decided by the
   * caller (`hasActuals` / `taskHasActuals`) from the watched status, so the Ist fields appear in
   * the same render as the user picking „Abgeschlossen".
   */
  showActuals: boolean;
  /** Marks „Termin um" required. A Termin always has one; an Aufgabe may have none. */
  startRequired?: boolean;
  /**
   * Marks „Arbeitszeit" required. Only where it is unconditionally so — the Aufgabe requires it
   * once a start is set, and a conditional rule is better told by the inline error than by a mark
   * that is wrong half the time.
   */
  workRequired?: boolean;
  /** Edit mode: the item belongs to a series, so the scope toggle applies. */
  hasSeries: boolean;
  /** Current `plannedStart` — the anchor the recurrence preview is derived from. */
  plannedStart: string;
  /** One line under the heading — the Aufgabe says what an empty Termin means. */
  hint?: string;
  /** Rendered under „Termin um", inside its cell — the Termin's Ganztägig checkbox. */
  startAddon?: ReactNode;
  /** The two segments of the edit-mode scope toggle; they carry the noun, which is why they are props. */
  seriesScopeLabels: { single: string; series: string };
}

export function ScheduleFields<T extends FieldValues & ScheduleFieldValues>({
  control,
  mode,
  showActuals,
  startRequired = false,
  workRequired = false,
  hasSeries,
  plannedStart,
  hint,
  startAddon,
  seriesScopeLabels,
}: ScheduleFieldsProps<T>) {
  // `Path<T>` cannot be resolved for a generic `T`; the constraint guarantees these keys exist.
  const field = (key: keyof ScheduleFieldValues) => key as unknown as Path<T>;

  // The planned Anfahrt/Abfahrt decide which Ist moments exist; the four Ist values feed the
  // read-only duration line beneath them.
  const [
    plannedTravelDuration,
    plannedDepartureDuration,
    travelStartedAt,
    actualStart,
    actualEnd,
    departureEndedAt,
  ] = useWatch({
    control,
    name: [
      field("plannedTravelDuration"),
      field("plannedDepartureDuration"),
      field("travelStartedAt"),
      field("actualStart"),
      field("actualEnd"),
      field("departureEndedAt"),
    ],
  }) as unknown as [number, number, string, string, string, string];

  const seriesItems = [
    { value: "single" as const, label: seriesScopeLabels.single },
    { value: "series" as const, label: seriesScopeLabels.series },
  ];

  return (
    <FieldGroup title="Zeit" hint={hint}>
      {/*
        One 6-column grid for the whole section rather than a 2-col row stacked on a 3-col row:
        6 divides by both, so the halves and the thirds share vertical edges instead of each row
        inventing its own gutter positions.
      */}
      <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-6">
        {/*
          The addon sits inside the time cell, not on its own row: it modifies the time rather than
          standing beside it, and as a separate grid item it backfilled the hole left by the
          2-wide duration cells and made the row ragged.
        */}
        <FieldWrapper label="Termin um" required={startRequired} className="col-span-2 sm:col-span-3">
          <FormInput name={field("plannedStart")} control={control} type="datetime-local" as="input" />
          {startAddon}
        </FieldWrapper>

        {/* col-start-1 keeps the durations on a row of their own whatever precedes them. */}
        <FieldWrapper label="Anfahrt (min)" className="sm:col-span-2 sm:col-start-1">
          <FormInput
            name={field("plannedTravelDuration")}
            control={control}
            type="number"
            as="input"
            step={15}
            min={0}
            valueAsNumber
          />
        </FieldWrapper>
        <FieldWrapper label="Arbeitszeit (min)" required={workRequired} className="sm:col-span-2">
          <FormInput
            name={field("plannedWorkDuration")}
            control={control}
            type="number"
            as="input"
            step={15}
            min={0}
            valueAsNumber
          />
        </FieldWrapper>
        <FieldWrapper label="Abfahrt (min)" className="sm:col-span-2">
          <FormInput
            name={field("plannedDepartureDuration")}
            control={control}
            type="number"
            as="input"
            step={15}
            min={0}
            valueAsNumber
          />
        </FieldWrapper>

        {/*
          Ist-Zeiten, only for a completed item. The caller's `showActuals` also gates its submit
          mapper — the two must not drift apart.

          Four **moments**, not three durations (Task 2040): the same instants the technician's
          taps stamp. The server derives the Ist-Minuten from them, which is what makes it
          impossible to close an item and leave its phase open. „Anfahrt ab" and „Abfahrt bis"
          only appear when the plan has such a phase; an absent one goes out as `null`.

          These are what the calendar draws once the item is completed: occupancy is
          `COALESCE(actual, planned)`, so a drag on the board retypes *these* inputs and not the
          planned ones above. Their own sub-heading says so — Plan stays visible above, so a
          correction is read against it.
        */}
        {showActuals && (
          <>
            <div className="col-span-2 mt-1 flex flex-col gap-0.5 border-t border-dashed border-slate-300 pt-3 sm:col-span-6">
              <h4 className="font-['Roboto'] text-[13px] font-medium leading-none text-[#124093]">
                Ist-Zeiten
              </h4>
              <p className="text-xs text-slate-400">Das zeichnet der Kalender. Vorbelegt mit dem Plan.</p>
            </div>
            {plannedTravelDuration > 0 && (
              <FieldWrapper label="Anfahrt ab" className="col-span-2 sm:col-span-3 sm:col-start-1">
                <FormInput
                  name={field("travelStartedAt")}
                  control={control}
                  type="datetime-local"
                  as="input"
                />
              </FieldWrapper>
            )}
            <FieldWrapper label="Arbeit ab" required className="col-span-2 sm:col-span-3">
              <FormInput name={field("actualStart")} control={control} type="datetime-local" as="input" />
            </FieldWrapper>
            <FieldWrapper label="Arbeit bis" required className="col-span-2 sm:col-span-3">
              <FormInput name={field("actualEnd")} control={control} type="datetime-local" as="input" />
            </FieldWrapper>
            {plannedDepartureDuration > 0 && (
              <FieldWrapper label="Abfahrt bis" className="col-span-2 sm:col-span-3">
                <FormInput
                  name={field("departureEndedAt")}
                  control={control}
                  type="datetime-local"
                  as="input"
                />
              </FieldWrapper>
            )}
            <ActualTimesSummary
              className="col-span-2 sm:col-span-6"
              instants={{
                travelStartedAt: travelStartedAt ?? "",
                actualStart: actualStart ?? "",
                actualEnd: actualEnd ?? "",
                departureEndedAt: departureEndedAt ?? "",
              }}
              fallback={{ travel: plannedTravelDuration, departure: plannedDepartureDuration }}
            />
          </>
        )}
      </div>

      {/*
        Serie belongs to the reservation, so it closes this section rather than opening one of its
        own. Create offers the rule; edit offers only the scope, because the frequency of an
        existing series is not patchable.

        `RecurrenceField` disables itself while `startDate` is empty — a series needs an anchor,
        and for an Aufgabe that is a state the form can actually be in.
      */}
      {mode === "create" ? (
        <Controller
          name={field("recurrence")}
          control={control}
          render={({ field: f }) => (
            <RecurrenceField
              startDate={plannedStart}
              value={(f.value ?? null) as RecurrenceInput | null}
              onChange={f.onChange}
              label="Als Serie wiederholen"
            />
          )}
        />
      ) : hasSeries ? (
        <div className="flex flex-col gap-2">
          <Controller
            name={field("forFullSeries")}
            control={control}
            render={({ field: f }) => (
              <SegmentedControl
                size="sm"
                className="self-start"
                items={seriesItems}
                value={f.value ? "series" : "single"}
                onChange={(next) => f.onChange(next === "series")}
                ariaLabel="Änderungsumfang"
              />
            )}
          />
          <p className="text-xs text-slate-400">Die Wiederholung selbst lässt sich nicht ändern.</p>
        </div>
      ) : null}
    </FieldGroup>
  );
}
